import { useState, useCallback, useEffect } from "react";
import * as authApi from "@api/auth.api";
import { parseKeyBundleJson } from "@/utils/keyBundle";

const VAULT_KEYS_PREFIX = "vault_key_";
const LOCAL_KEY_CACHE = "vault_local_key";

/** Raw key bundle JSON, applied after sign-in (needs server local key to encrypt). */
export const PENDING_KEY_IMPORT_STORAGE_KEY = "_pending_key_import";

export const LOCAL_KEYS_UPDATED_EVENT = "securevault:local-keys-updated";

export function stashPendingKeyImport(bundleJson: string): void {
  localStorage.setItem(PENDING_KEY_IMPORT_STORAGE_KEY, bundleJson);
}

async function getLocalKey(): Promise<string> {
  const cached = sessionStorage.getItem(LOCAL_KEY_CACHE);
  if (cached) return cached;

  const key = await authApi.getLocalKey();
  sessionStorage.setItem(LOCAL_KEY_CACHE, key);
  return key;
}

async function encryptForStorage(
  data: string,
  keyHex: string,
): Promise<string> {
  const keyBytes = hexToBytes(keyHex).slice(0, 32);
  const key = await crypto.subtle.importKey("raw", keyBytes, "AES-GCM", false, [
    "encrypt",
  ]);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const encrypted = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    key,
    new TextEncoder().encode(data),
  );
  const combined = new Uint8Array(iv.length + encrypted.byteLength);
  combined.set(iv);
  combined.set(new Uint8Array(encrypted), iv.length);
  return bytesToHex(combined);
}

async function decryptFromStorage(
  hex: string,
  keyHex: string,
): Promise<string> {
  const bytes = hexToBytes(hex);
  const keyBytes = hexToBytes(keyHex).slice(0, 32);
  const key = await crypto.subtle.importKey("raw", keyBytes, "AES-GCM", false, [
    "decrypt",
  ]);
  const iv = bytes.slice(0, 12);
  const data = bytes.slice(12);
  const decrypted = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv },
    key,
    data,
  );
  return new TextDecoder().decode(decrypted);
}

function hexToBytes(hex: string): Uint8Array {
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < hex.length; i += 2) {
    bytes[i / 2] = parseInt(hex.substring(i, i + 2), 16);
  }
  return bytes;
}

function bytesToHex(bytes: Uint8Array): string {
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

/** After login: merge pending bundle into vault using the user's local key. Returns number of keys stored. */
export async function consumePendingKeyImport(): Promise<number> {
  const raw = localStorage.getItem(PENDING_KEY_IMPORT_STORAGE_KEY);
  if (!raw?.trim()) return 0;
  const bundle = parseKeyBundleJson(raw);
  if (!bundle) {
    localStorage.removeItem(PENDING_KEY_IMPORT_STORAGE_KEY);
    return 0;
  }
  try {
    const localKey = await getLocalKey();
    let count = 0;
    for (const [imageId, unlockKey] of Object.entries(bundle)) {
      const stored = await encryptForStorage(unlockKey, localKey);
      localStorage.setItem(`${VAULT_KEYS_PREFIX}${imageId}`, stored);
      count++;
    }
    localStorage.removeItem(PENDING_KEY_IMPORT_STORAGE_KEY);
    window.dispatchEvent(new Event(LOCAL_KEYS_UPDATED_EVENT));
    return count;
  } catch {
    return 0;
  }
}

export function useLocalKeys() {
  const [keyCount, setKeyCount] = useState(0);

  const refreshCount = useCallback(() => {
    let count = 0;
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k?.startsWith(VAULT_KEYS_PREFIX)) count++;
    }
    setKeyCount(count);
  }, []);

  useEffect(() => {
    refreshCount();
  }, [refreshCount]);

  useEffect(() => {
    const onUpdate = () => refreshCount();
    window.addEventListener(LOCAL_KEYS_UPDATED_EVENT, onUpdate);
    return () => window.removeEventListener(LOCAL_KEYS_UPDATED_EVENT, onUpdate);
  }, [refreshCount]);

  const storeKey = useCallback(
    async (imageId: string, encryptedUnlockKey: string) => {
      const localKey = await getLocalKey();
      const stored = await encryptForStorage(encryptedUnlockKey, localKey);
      localStorage.setItem(`${VAULT_KEYS_PREFIX}${imageId}`, stored);
      refreshCount();
      window.dispatchEvent(new Event(LOCAL_KEYS_UPDATED_EVENT));
    },
    [refreshCount],
  );

  const getKey = useCallback(
    async (imageId: string): Promise<string | null> => {
      const stored = localStorage.getItem(`${VAULT_KEYS_PREFIX}${imageId}`);
      if (!stored) return null;
      const localKey = await getLocalKey();
      return decryptFromStorage(stored, localKey);
    },
    [],
  );

  const removeKey = useCallback(
    (imageId: string) => {
      localStorage.removeItem(`${VAULT_KEYS_PREFIX}${imageId}`);
      refreshCount();
      window.dispatchEvent(new Event(LOCAL_KEYS_UPDATED_EVENT));
    },
    [refreshCount],
  );

  const hasKey = useCallback((imageId: string): boolean => {
    return localStorage.getItem(`${VAULT_KEYS_PREFIX}${imageId}`) !== null;
  }, []);

  const getAllKeyIds = useCallback((): string[] => {
    const ids: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k?.startsWith(VAULT_KEYS_PREFIX)) {
        ids.push(k.replace(VAULT_KEYS_PREFIX, ""));
      }
    }
    return ids;
  }, []);

  const exportBundle = useCallback(async (): Promise<string> => {
    const localKey = await getLocalKey();
    const bundle: Record<string, string> = {};
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k?.startsWith(VAULT_KEYS_PREFIX)) {
        const imageId = k.replace(VAULT_KEYS_PREFIX, "");
        const stored = localStorage.getItem(k)!;
        bundle[imageId] = await decryptFromStorage(stored, localKey);
      }
    }
    return JSON.stringify(bundle);
  }, []);

  const importBundle = useCallback(
    async (bundleJson: string) => {
      const bundle = parseKeyBundleJson(bundleJson);
      if (!bundle) {
        throw new Error("Invalid key bundle file.");
      }
      const localKey = await getLocalKey();
      for (const [imageId, unlockKey] of Object.entries(bundle)) {
        const stored = await encryptForStorage(unlockKey, localKey);
        localStorage.setItem(`${VAULT_KEYS_PREFIX}${imageId}`, stored);
      }
      refreshCount();
      window.dispatchEvent(new Event(LOCAL_KEYS_UPDATED_EVENT));
    },
    [refreshCount],
  );

  return {
    keyCount,
    storeKey,
    getKey,
    removeKey,
    hasKey,
    getAllKeyIds,
    exportBundle,
    importBundle,
  };
}
